import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Space, 
  List, 
  Tag, 
  Alert,
  Empty,
  Spin,
  Collapse,
  Pagination
} from 'antd';
import { 
  ClockCircleOutlined,
  DownOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../../analytics';
import fetchChecksList from '../../utils/drugInteraction/fetchChecksList';

const { Text } = Typography;
const { Panel } = Collapse;

const DrugInteractionsHistoryTab = () => {
  const { t } = useTranslation();
  
  // History tab state
  const [historyChecks, setHistoryChecks] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const loadHistory = async (page = 1) => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    
    try {
      const { checks, pagination } = await fetchChecksList(page);
      setHistoryChecks(checks);
      setHistoryPagination(pagination);
      
      logEvent('DrugInteractions', 'LoadHistory', { 
        checkCount: checks.length,
        page: page
      });
    } catch (error) {
      console.error('Failed to load history:', error);
      setHistoryError(error.message);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleHistoryPageChange = (page) => {
    loadHistory(page);
  };

  // Load history when component mounts
  useEffect(() => {
    loadHistory(1);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'major': return 'red';
      case 'moderate': return 'orange';
      case 'minor': return 'default';
      default: return 'default';
    }
  };

  const getSeverityText = (severity) => {
    switch (severity) {
      case 'major': return t('drug_interactions.severity.major');
      case 'moderate': return t('drug_interactions.severity.moderate');
      case 'minor': return t('drug_interactions.severity.minor');
      case 'none': return t('drug_interactions.severity.none');
      default: return severity;
    }
  };

  const getSeverityOrder = (severity) => {
    switch (severity) {
      case 'major': return 0;
      case 'moderate': return 1;
      case 'minor': return 2;
      case 'none': return 3;
      default: return 4;
    }
  };

  const sortInteractionsBySeverity = (interactions) => {
    return [...interactions].sort((a, b) => {
      const orderA = getSeverityOrder(a.severity);
      const orderB = getSeverityOrder(b.severity);
      return orderA - orderB;
    });
  };

  return (
    <div data-testid="drug-interactions-history">
      {isLoadingHistory ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">{t('drug_interactions.loading_history')}</Text>
          </div>
        </div>
      ) : historyError ? (
        <Alert
          data-testid="history-error-alert"
          message={t('drug_interactions.history_error')}
          description={historyError}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      ) : historyChecks.length > 0 ? (
        <div>
          <Collapse
            data-testid="history-checks-collapse"
            collapsible="header"
            defaultActiveKey={[]}
            expandIcon={({ isActive }) => (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: isActive ? '#666' : '#f0f0f0',
                color: isActive ? 'white' : '#666',
                fontSize: '12px',
                transition: 'all 0.2s'
              }}>
                {isActive ? <DownOutlined /> : <RightOutlined />}
              </div>
            )}
            ghost
            style={{ 
              backgroundColor: 'transparent',
              border: 'none'
            }}
          >
            {historyChecks.map((check, index) => (
              <Panel
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: '14px', display: 'block' }}>
                        {check.medicationsString || t('drug_interactions.unknown_medications')}
                      </Text>
                      <Space style={{ marginTop: 4 }}>
                        <ClockCircleOutlined style={{ color: '#999', fontSize: '14px' }} />
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          {check.completed_at ? new Date(check.completed_at).toLocaleDateString() : t('drug_interactions.unknown_date')}
                        </Text>
                        {check.getPatient() && (
                          <>
                            <Text type="secondary" style={{ fontSize: '14px' }}>•</Text>
                            <Text type="secondary" style={{ fontSize: '14px' }}>
                              {check.getPatient().getFullName()}
                            </Text>
                          </>
                        )}
                      </Space>
                    </div>
                  </div>
                }
                key={index}
                style={{ marginBottom: 8 }}
                className="history-check-panel"
                data-testid="history-check-panel"
              >
                <div style={{ marginTop: 16 }}>
                  {check.hasNoInteractions() ? (
                    <Alert
                      message={t('drug_interactions.no_interactions_found')}
                      description={t('drug_interactions.no_interactions_description')}
                      type="success"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  ) : (
                    <div>
                      <List
                        dataSource={sortInteractionsBySeverity(check.getInteractions())}
                        renderItem={(interaction, interactionIndex) => (
                          <div 
                            style={{ 
                              border: '1px solid #f0f0f0', 
                              borderRadius: '6px',
                              padding: '12px',
                              marginBottom: '12px',
                              backgroundColor: '#fafafa'
                            }}
                          >
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                                <Tag color={getSeverityColor(interaction.severity)} style={{ fontSize: '12px' }}>
                                  {getSeverityText(interaction.severity)}
                                </Tag>
                              </div>
                              
                              <Text style={{ fontSize: '15px', color: '#666', lineHeight: '1.5' }}>
                                {interaction.description}
                              </Text>
                              
                              {/* Patient-Specific Recommendation FIRST, green */}
                              {interaction.recommendationPatientSpecific && (interaction.severity === 'major' || interaction.severity === 'moderate') && (
                                <div style={{ 
                                  backgroundColor: '#f6ffed', // green background
                                  padding: '8px 12px', 
                                  borderRadius: '4px',
                                  border: '1px solid #b7eb8f', // green border
                                  marginBottom: interaction.recommendationGeneral ? '8px' : '0'
                                }}>
                                  <Text style={{ fontSize: '14px', color: '#333', fontStyle: 'italic', lineHeight: '1.4' }}>
                                    <strong>{t('drug_interactions.patient_specific_recommendation')}:</strong> {interaction.recommendationPatientSpecific}
                                  </Text>
                                </div>
                              )}

                              {/* General Recommendation SECOND, blue */}
                              {interaction.recommendationGeneral && (interaction.severity === 'major' || interaction.severity === 'moderate') && (
                                <div style={{ 
                                  backgroundColor: '#e6f7ff', // blue background
                                  padding: '8px 12px', 
                                  borderRadius: '4px',
                                  border: '1px solid #91d5ff', // blue border
                                }}>
                                  <Text style={{ fontSize: '14px', color: '#333', fontStyle: 'italic', lineHeight: '1.4' }}>
                                    <strong>{t('drug_interactions.recommendation')}:</strong> {interaction.recommendationGeneral}
                                  </Text>
                                </div>
                              )}
                            </Space>
                          </div>
                        )}
                      />
                    </div>
                  )}
                </div>
              </Panel>
            ))}
          </Collapse>
          
          {/* Pagination */}
          {historyPagination.total > historyPagination.pageSize && (
            <div style={{ textAlign: 'center', marginTop: 20, padding: '16px 0' }}>
              <Pagination
                current={historyPagination.current}
                pageSize={historyPagination.pageSize}
                total={historyPagination.total}
                onChange={handleHistoryPageChange}
                showSizeChanger={false}
                showTotal={(total, range) => 
                  `${range[0]}-${range[1]} of ${total} checks`
                }
              />
            </div>
          )}
        </div>
      ) : (
        <Empty
          data-testid="no-history-empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('drug_interactions.no_history')}
          style={{ padding: '40px 0' }}
        />
      )}
    </div>
  );
};

export default DrugInteractionsHistoryTab; 